import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Data controllers
import { getAppSettings, updateAppSettings, resetAppSettings, sendEmailOTP, resetPassword } from "data/controllers/settingsController";

function Settings() {
  // Settings state
  const [settings, setSettings] = useState({
    allowNewRegistrations: true,
    allowAllLogins: true,
    appEnabled: true,
  });

  // UI state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getAppSettings();
      if (response.success) {
        setSettings(response.data);
      } else {
        showSnackbar(response.message || "Error loading settings", "error");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      showSnackbar("Error loading settings", "error");
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      const response = await updateAppSettings(newSettings);
      if (response.success) {
        setSettings(response.data);
        showSnackbar(response.message || "Settings saved successfully", "success");
      } else {
        showSnackbar(response.message || "Error saving settings", "error");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      showSnackbar("Error saving settings", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSettingChange = (settingKey, value) => {
    // Show confirmation dialog for all settings changes
    let title = "";
    let message = "";
    let description = "";

    switch (settingKey) {
      case "allowNewRegistrations":
        title = value ? "Confirm Enable New Registrations" : "Confirm Disable New Registrations";
        message = value
          ? "Are you sure you want to allow new users to register?"
          : "Are you sure you want to stop new user registrations?";
        description = value
          ? "This will allow new users to create accounts and access the application."
          : "Existing users will still be able to log in and use the app, but no new accounts can be created.";
        break;
      case "allowAllLogins":
        title = value ? "Confirm Enable User Logins" : "Confirm Disable User Logins";
        message = value
          ? "Are you sure you want to allow user logins?"
          : "Are you sure you want to disable all user logins?";
        description = value
          ? "This will allow all users to log in to the application."
          : "This will prevent users from logging in, but existing logged-in users can continue using the app.";
        break;
      case "appEnabled":
        title = value ? "Confirm Enable Application" : "Confirm Disable Application";
        message = value
          ? "Are you sure you want to enable the application?"
          : "Are you sure you want to disable the entire application?";
        description = value
          ? "This will restore full application functionality for all users."
          : "This will shut down the app for all users except administrators. This action should only be used in emergency situations.";
        break;
      default:
        return;
    }

    setDialogTitle(title);
    setDialogMessage(message);
    setPendingChange({ key: settingKey, value, description });
    setConfirmDialogOpen(true);
  };

  const handleConfirmChange = () => {
    if (pendingChange) {
      let newSettings = { ...settings, [pendingChange.key]: pendingChange.value };

      // Smart cascading logic for settings
      if (pendingChange.key === "appEnabled") {
        if (!pendingChange.value) {
          // If disabling the app, automatically disable other settings too
          newSettings = {
            ...newSettings,
            allowNewRegistrations: false,
            allowAllLogins: false,
          };
        } else {
          // If enabling the app, automatically enable other settings too
          newSettings = {
            ...newSettings,
            allowNewRegistrations: true,
            allowAllLogins: true,
          };
        }
      }

      saveSettings(newSettings);
      setPendingChange(null);
    }
    setConfirmDialogOpen(false);
  };

  const handleCancelChange = () => {
    setPendingChange(null);
    setConfirmDialogOpen(false);
  };

  const handleResetSettings = () => {
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    try {
      const response = await updateAppSettings({
        allowNewRegistrations: true, allowAllLogins: true, appEnabled: true
      });
      if (response.success) {
        setSettings(response.data);
        showSnackbar(response.message || "Settings reset to default", "success");
      } else {
        showSnackbar(response.message || "Error resetting settings", "error");
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      showSnackbar("Error resetting settings", "error");
    }
    setResetDialogOpen(false);
  };

  const handleCancelReset = () => {
    setResetDialogOpen(false);
  };

  const getSettingDescription = (key) => {
    const descriptions = {
      allowNewRegistrations: "When disabled, new users cannot register but existing users can still log in and use the app.",
      allowAllLogins: "When disabled, all user logins are blocked but existing logged-in users can continue using the app.",
      appEnabled: "When disabled, the entire application is shut down for all users except administrators.",
    };
    return descriptions[key] || "";
  };

  const getSettingStatus = () => {
    if (!settings.appEnabled) {
      return { text: "App is completely disabled", color: "error" };
    }
    if (!settings.allowAllLogins) {
      return { text: "Logins are disabled", color: "warning" };
    }
    if (!settings.allowNewRegistrations) {
      return { text: "New registrations are disabled", color: "info" };
    }
    return { text: "All systems operational", color: "success" };
  };

  const status = getSettingStatus();

  // Security State
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [securityStep, setSecurityStep] = useState(0); // 0: Confirm Send OTP, 1: Enter OTP & New Password
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleSecurityClick = () => {
    setSecurityStep(0);
    setSecurityDialogOpen(true);
  };

  const handleSendOTP = async () => {
    const response = await sendEmailOTP();
    if (response.success) {
      showSnackbar(response.message, "success");
      setSecurityStep(1);
    } else {
      showSnackbar(response.message, "error");
    }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword) {
      showSnackbar("Please enter limit fields", "error");
      return;
    }

    const response = await resetPassword(otp, newPassword);
    if (response.success) {
      showSnackbar(response.message, "success");
      setSecurityDialogOpen(false);
      setOtp("");
      setNewPassword("");
    } else {
      showSnackbar(response.message, "error");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Application Settings
                </MDTypography>
              </MDBox>

              {/* Status Alert */}
              <MDBox p={3}>
                <Alert severity={status.color} sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Current Status:</strong> {status.text}
                  </Typography>
                </Alert>

                {/* Settings Controls */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <MDBox mb={2}>
                        <MDTypography variant="h6" gutterBottom>
                          Registration Control
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mb={2}>
                          {!settings.appEnabled
                            ? "This setting is disabled because the application is currently shut down."
                            : getSettingDescription("allowNewRegistrations")
                          }
                        </MDTypography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allowNewRegistrations}
                              onChange={(e) => handleSettingChange("allowNewRegistrations", e.target.checked)}
                              color="primary"
                              disabled={!settings.appEnabled}
                            />
                          }
                          label={settings.allowNewRegistrations ? "New Registrations Enabled" : "New Registrations Disabled"}
                        />
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <MDBox mb={2}>
                        <MDTypography variant="h6" gutterBottom>
                          Login Control
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mb={2}>
                          {!settings.appEnabled
                            ? "This setting is disabled because the application is currently shut down."
                            : getSettingDescription("allowAllLogins")
                          }
                        </MDTypography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allowAllLogins}
                              onChange={(e) => handleSettingChange("allowAllLogins", e.target.checked)}
                              color="primary"
                              disabled={!settings.appEnabled}
                            />
                          }
                          label={settings.allowAllLogins ? "User Logins Enabled" : "User Logins Disabled"}
                        />
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 3, borderColor: "error.main" }}>
                      <MDBox mb={2}>
                        <MDTypography variant="h6" gutterBottom color="error">
                          Emergency Shutdown
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mb={2}>
                          {getSettingDescription("appEnabled")}
                        </MDTypography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.appEnabled}
                              onChange={(e) => handleSettingChange("appEnabled", e.target.checked)}
                              color="error"
                            />
                          }
                          label={settings.appEnabled ? "Application Enabled" : "Application Disabled"}
                        />
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <MDBox mb={2}>
                        <MDTypography variant="h6" gutterBottom>
                          Security
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mb={2}>
                          Change your password securely by verifying your email address.
                        </MDTypography>
                        <Button variant="contained" color="primary" onClick={handleSecurityClick} style={{ color: '#ffffff' }}>
                          Reset Password
                        </Button>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="center" mt={3}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetSettings}
                        sx={{
                          minWidth: "200px",
                          height: "40px",
                          fontSize: "14px",
                          fontWeight: "medium",
                        }}
                      >
                        <MDTypography variant="button" color="secondary" fontWeight="medium">
                          Reset to Default Settings
                        </MDTypography>
                      </Button>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {dialogMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {pendingChange?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChange} color="primary">
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmChange}
            variant="contained"
            sx={{
              backgroundColor: "error.main",
              color: "white !important",
              "&:hover": {
                backgroundColor: "error.dark",
                color: "white !important",
              },
            }}
          >
            {pendingChange?.key === "appEnabled" && !pendingChange?.value ? "CONFIRM SHUTDOWN" : "CONFIRM"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCancelReset}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Reset Settings to Default</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reset all settings to their default values?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will restore the following default settings: New registrations enabled, User logins enabled, and Application enabled. All current configuration will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset} color="primary">
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmReset}
            variant="contained"
            sx={{
              backgroundColor: "error.main",
              color: "white !important",
              "&:hover": {
                backgroundColor: "error.dark",
                color: "white !important",
              },
            }}
          >
            CONFIRM RESET
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Dialog */}
      <Dialog
        open={securityDialogOpen}
        onClose={() => setSecurityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{securityStep === 0 ? "Reset Password" : "Enter OTP & New Password"}</DialogTitle>
        <DialogContent>
          {securityStep === 0 ? (
            <Typography fontSize="16px">
              Click 'Send OTP' to receive a verification code on your registered email address.
            </Typography>
          ) : (
            <MDBox component="form" role="form">
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold">OTP</MDTypography>
                <input
                  type="text"
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                />
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold">New Password</MDTypography>
                <input
                  type="password"
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter New Password"
                />
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityDialogOpen(false)} color="primary">
            CANCEL
          </Button>
          {securityStep === 0 ? (
            <Button
              onClick={handleSendOTP}
              variant="contained"
              color="primary"
              sx={{ color: '#fff' }}
              style={{ color: '#ffffff' }}
            >
              SEND OTP
            </Button>
          ) : (
            <Button
              onClick={handleResetPassword}
              variant="contained"
              color="primary"
              sx={{ color: '#fff' }}
              style={{ color: '#ffffff' }}
            >
              RESET PASSWORD
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Settings;
